import { PhoneNumberFormat, PhoneNumberType, PhoneNumberUtil } from "google-libphonenumber";
import { isProductionEnvironment } from "@remi/environment";
import { RemiPackage } from "@remi/error/remi-error/errorTypes/remi-package";
import { RemiError } from "@remi/error/remi-error/RemiError";
import { ErrorSeverity } from "@remi/error/sentry/severity";

// NANP reserves 555-0100 through 555-0199 for fictional use: no carrier can assign them, while a real geographic
// area code keeps them valid for libphonenumber and for vendors that validate recipient phones with it.
const FICTIONAL_EXCHANGE = "555";
const FICTIONAL_FIRST_LINE = 100;
const FICTIONAL_LINES_PER_AREA_CODE = 100;
// Built on first use: the scan makes 800 libphonenumber calls, too slow to run when the mobile app loads this module.
let fictionalAreaCodes: string[] | undefined;
const getFictionalAreaCodes = (): string[] => {
  if (fictionalAreaCodes) {
    return fictionalAreaCodes;
  }
  const util = PhoneNumberUtil.getInstance();
  const areaCodes: string[] = [];
  for (let areaCode = 200; areaCode <= 999; areaCode += 1) {
    const parsed = util.parse(`+1${areaCode}${FICTIONAL_EXCHANGE}0${FICTIONAL_FIRST_LINE}`, "US");
    const isGeographicUs =
      util.isValidNumber(parsed) &&
      util.getRegionCodeForNumber(parsed) === "US" &&
      util.getNumberType(parsed) === PhoneNumberType.FIXED_LINE_OR_MOBILE;
    if (isGeographicUs) {
      areaCodes.push(String(areaCode));
    }
  }
  fictionalAreaCodes = areaCodes;
  return areaCodes;
};

export const getFictionalPhoneSpace = () => ({
  areaCodes: getFictionalAreaCodes(),
  exchange: FICTIONAL_EXCHANGE,
  firstLine: FICTIONAL_FIRST_LINE,
  linesPerAreaCode: FICTIONAL_LINES_PER_AREA_CODE,
});

export const getFictionalPhoneNumberCapacity = (): number =>
  getFictionalAreaCodes().length * FICTIONAL_LINES_PER_AREA_CODE;

let fictionalPhoneNumberPattern: RegExp | undefined;
const getFictionalPhoneNumberPattern = (): RegExp => {
  // Matches generated numbers and the legacy +1555XXXXXXX form.
  fictionalPhoneNumberPattern ??= new RegExp(
    `^\\+1(?:(?:${getFictionalAreaCodes().join("|")})${FICTIONAL_EXCHANGE}01\\d{2}|${FICTIONAL_EXCHANGE}\\d{7})$`,
  );
  return fictionalPhoneNumberPattern;
};

export const getFictionalPhoneNumber = (index: number): string => {
  const capacity = getFictionalPhoneNumberCapacity();
  if (!Number.isInteger(index) || index < 0 || index >= capacity) {
    throw new RemiError({
      remiPackage: RemiPackage.FORMATTING,
      errorType: "FICTIONAL_PHONE_NUMBER_INDEX_OUT_OF_RANGE",
      severity: ErrorSeverity.HIGH,
      data: { index, capacity },
    });
  }
  const areaCode = getFictionalAreaCodes()[Math.floor(index / FICTIONAL_LINES_PER_AREA_CODE)];
  const line = String(FICTIONAL_FIRST_LINE + (index % FICTIONAL_LINES_PER_AREA_CODE)).padStart(4, "0");
  return `+1${areaCode}${FICTIONAL_EXCHANGE}${line}`;
};

export const getRandomFictionalPhoneNumber = (): string =>
  getFictionalPhoneNumber(Math.floor(Math.random() * getFictionalPhoneNumberCapacity()));

export const isFictionalPhoneNumber = (phoneNumber: string): boolean =>
  getFictionalPhoneNumberPattern().test(phoneNumber);

export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  try {
    const util = PhoneNumberUtil.getInstance();
    const parsed = util.parse(phoneNumber, "US");
    const formatted = util.format(parsed, PhoneNumberFormat.E164);
    if (!isProductionEnvironment() && isFictionalPhoneNumber(formatted)) {
      return true;
    }
    return util.isValidNumber(parsed);
  } catch {
    return false;
  }
};

export const formatPhoneNumberForDatabase = (phoneNumber: string): string => {
  //Formats phone numbers to E.164 format assuming US number, otherwise it returns the input string
  const digitsOnly = phoneNumber.replace(/\D/g, "");
  if (digitsOnly.length === 11 && digitsOnly[0] === "1") {
    phoneNumber = digitsOnly.slice(1);
  }

  try {
    const util = PhoneNumberUtil.getInstance();
    const parsed = util.parse(phoneNumber, "US");
    return util.format(parsed, PhoneNumberFormat.E164);
  } catch {
    return phoneNumber;
  }
};

type FormatPhoneNumberForDisplayOptions = {
  extension?: string | null;
  format?: PhoneNumberFormat;
};

export const formatPhoneNumberForDisplay = (
  phoneNumber: string,
  options?: FormatPhoneNumberForDisplayOptions,
): string => {
  try {
    const util = PhoneNumberUtil.getInstance();
    const parsed = util.parse(formatPhoneNumberForDatabase(phoneNumber), "E.164");
    return (
      util.format(parsed, options?.format ?? PhoneNumberFormat.INTERNATIONAL) +
      (options?.extension ? ` Ext ${options.extension}` : "")
    );
  } catch {
    return phoneNumber;
  }
};

export const formatPhoneNumberForDisplayNational = (phoneNumber: string): string =>
  formatPhoneNumberForDisplay(phoneNumber, { format: PhoneNumberFormat.NATIONAL });

/**
 * Strips all non-numeric characters from a phone string, keeping only digits
 * and a leading '+' for international numbers.
 */
export const stripPhoneToDigits = (phone: string | undefined): string | undefined => {
  if (!phone) {
    return phone;
  }
  const stripped = phone.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
  return stripped.length > 0 ? stripped : undefined;
};

export const formatPhoneNumberDigitsOnly = (phoneNumber: string): string => {
  return phoneNumber.replace(/\D/g, "");
};

export const formatPhoneNumberNoCountryCode = (phoneNumber: string): string => {
  return formatPhoneNumberDigitsOnly(phoneNumber).slice(-10);
};

export const formatPhoneNumberForDisplayRealtime = (rawValue: string): string => {
  if (!rawValue) {
    return "";
  }

  let digitsOnly = rawValue.replace(/\D/g, "");

  // Handle US country code: if 11 digits and starts with 1, remove the leading 1
  if (digitsOnly.length === 11 && digitsOnly[0] === "1") {
    digitsOnly = digitsOnly.slice(1);
  }

  if (digitsOnly.length <= 3) {
    return digitsOnly;
  } else if (digitsOnly.length <= 6) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
  } else if (digitsOnly.length <= 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
  } else {
    // For more than 10 digits, truncate to first 10
    const truncated = digitsOnly.slice(0, 10);
    return `(${truncated.slice(0, 3)}) ${truncated.slice(3, 6)}-${truncated.slice(6, 10)}`;
  }
};

export const getRawPhoneNumber = (formattedValue: string): string => {
  return formattedValue.replace(/\D/g, "");
};

export { PhoneNumberFormat };
